import { useState, useEffect, useMemo, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { ColorPicker } from '@/components/ui/color-picker';
import { usePlotsStore } from '@/store/plots';
import { getTraceCapabilities } from '../capabilities';
import {
  CircleIcon,
  SquareIcon,
  DiamondIcon,
  TriangleUpIcon,
  TriangleDownIcon,
  CrossIcon,
  XIcon,
  HexagonIcon,
  StarIcon,
  YUpIcon
} from '@/components/icons/symbol-icons';

interface SeriesControlsProps {
  plotlyJson?: any;
  onUpdate: (updateType: string, value: any) => void;
}

// Symbol options with icons
const symbolOptions = [
  { value: 'circle', label: 'Circle', Icon: CircleIcon },
  { value: 'square', label: 'Square', Icon: SquareIcon },
  { value: 'diamond', label: 'Diamond', Icon: DiamondIcon },
  { value: 'triangle-up', label: 'Triangle Up', Icon: TriangleUpIcon },
  { value: 'triangle-down', label: 'Triangle Down', Icon: TriangleDownIcon },
  { value: 'cross', label: 'Cross', Icon: CrossIcon },
  { value: 'x', label: 'X', Icon: XIcon },
  { value: 'hexagon', label: 'Hexagon', Icon: HexagonIcon },
  { value: 'star', label: 'Star', Icon: StarIcon },
  { value: 'y-up', label: 'Y (Up)', Icon: YUpIcon },
];

export function SeriesControls({ plotlyJson, onUpdate }: SeriesControlsProps) {
  const { selectedSeries } = usePlotsStore();
  const traces = plotlyJson?.data || [];
  const [selectedTrace, setSelectedTrace] = useState(0);
  
  // Update selectedTrace when series is clicked on the plot
  useEffect(() => {
    if (selectedSeries !== null && selectedSeries !== undefined && selectedSeries < traces.length) {
      setSelectedTrace(selectedSeries);
    }
  }, [selectedSeries, traces.length]);
  
  // Memoize currentTrace to ensure reactivity when plotlyJson updates
  const currentTrace = useMemo(() => {
    return traces[selectedTrace] || {};
  }, [traces, selectedTrace]);
  
  const caps = useMemo(() => getTraceCapabilities(currentTrace as any), [currentTrace]);

  const [lineWidth, setLineWidth] = useState(currentTrace.line?.width || 2);
  const [itemSize, setItemSize] = useState(currentTrace.type === 'bar' ? 16 : 8);
  const [gap, setGap] = useState(plotlyJson?.layout?.bargap || 0.2);
  const modeHasMarkers = useMemo(() => !!currentTrace?.mode && String(currentTrace.mode).includes('markers'), [currentTrace?.mode]);
  const [showMarkers, setShowMarkers] = useState(modeHasMarkers);
  const [selectedSymbol, setSelectedSymbol] = useState(currentTrace.marker?.symbol || 'circle');
  
  // Fix stale state bug: sync lineWidth when selectedTrace changes
  useEffect(() => {
    const newLineWidth = currentTrace.line?.width || 2;
    setLineWidth(newLineWidth);
  }, [selectedTrace, currentTrace.line?.width]);
  // Normalize color value to hex format for consistent comparison
  // Handles rgba(), hex with/without opacity, and array colors
  const normalizeColorValue = (v: any, fallback: string): string => {
    if (!v || (typeof v !== 'string' && !Array.isArray(v))) return fallback;
    
    // Handle array colors - use first element
    if (Array.isArray(v) && v.length > 0) {
      v = v[0];
      if (typeof v !== 'string') return fallback;
    }
    
    // Handle rgba() format - convert to hex
    if (v.trim().toLowerCase().startsWith('rgba(')) {
      const m = v.match(/rgba\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([0-1]?(?:\.\d+)?)\)/i);
      if (m) {
        const r = Math.max(0, Math.min(255, parseInt(m[1], 10)));
        const g = Math.max(0, Math.min(255, parseInt(m[2], 10)));
        const b = Math.max(0, Math.min(255, parseInt(m[3], 10)));
        const toHex = (n: number) => n.toString(16).padStart(2, '0');
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
      }
    }
    
    // Handle rgb() format - convert to hex
    if (v.trim().toLowerCase().startsWith('rgb(')) {
      const m = v.match(/rgb\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)\)/i);
      if (m) {
        const r = Math.max(0, Math.min(255, parseInt(m[1], 10)));
        const g = Math.max(0, Math.min(255, parseInt(m[2], 10)));
        const b = Math.max(0, Math.min(255, parseInt(m[3], 10)));
        const toHex = (n: number) => n.toString(16).padStart(2, '0');
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
      }
    }
    
    // Handle hex with opacity suffix (#RRGGBBAA) - strip opacity
    if (v.length === 9 && v.startsWith('#')) {
      return v.slice(0, 7);
    }
    
    // Return hex as-is if valid
    if (typeof v === 'string' && v.length > 0 && (v.startsWith('#') || /^[0-9a-f]{6}$/i.test(v))) {
      return v.startsWith('#') ? v : `#${v}`;
    }
    
    return fallback;
  };
  const [fillColor, setFillColor] = useState(() => {
    // For Indicator traces, read from gauge.bar.color
    if (String(currentTrace?.type || '').toLowerCase() === 'indicator') {
      return normalizeColorValue(currentTrace.gauge?.bar?.color, '#1f77b4');
    }
    // For scatter traces with fill (like ridgeline plots), prioritize fillcolor
    const isScatterWithFill = (currentTrace?.type === 'scatter' || !currentTrace?.type) && currentTrace?.fill;
    if (isScatterWithFill && currentTrace.fillcolor) {
      // Remove opacity suffix if present (2-digit hex at end)
      const fillcolor = String(currentTrace.fillcolor);
      const withoutOpacity = fillcolor.length === 9 && fillcolor.startsWith('#') 
        ? fillcolor.slice(0, 7) 
        : fillcolor;
      return normalizeColorValue(withoutOpacity, '#1f77b4');
    }
    return normalizeColorValue(currentTrace.marker?.color ?? currentTrace.fillcolor, '#1f77b4');
  });
  const [lineColor, setLineColor] = useState(
    normalizeColorValue(currentTrace.line?.color ?? currentTrace.marker?.line?.color, '#333333')
  );
  const [decoupleLine, setDecoupleLine] = useState(currentTrace.line?.decoupled || false);
  
  // Label font size state
  const [labelFontSize, setLabelFontSize] = useState(
    currentTrace.textfont?.size || plotlyJson?.layout?.font?.size || 12
  );
  
  // Update label font size when trace changes
  useEffect(() => {
    const newSize = currentTrace.textfont?.size || plotlyJson?.layout?.font?.size || 12;
    setLabelFontSize(newSize);
  }, [currentTrace, plotlyJson?.layout?.font?.size]);

  // Track previous color to prevent unnecessary updates
  const prevColorRef = useRef<string>('');
  
  // Extract color from trace preserving format (rgba/hex) for ColorPicker display
  const extractColorFromTrace = (trace: any): string => {
    const isIndicatorTrace = String(trace?.type || '').toLowerCase() === 'indicator';
    
    if (isIndicatorTrace) {
      return trace.gauge?.bar?.color || '#1f77b4';
    }
    
    // For scatter traces with fill (like ridgeline plots), prioritize fillcolor
    const isScatterWithFill = (trace?.type === 'scatter' || !trace?.type) && trace?.fill;
    if (isScatterWithFill && trace.fillcolor) {
      // Remove opacity suffix if present (2-digit hex at end) but preserve rgba()
      const fillcolor = String(trace.fillcolor);
      if (fillcolor.toLowerCase().startsWith('rgba(')) {
        return fillcolor; // Keep rgba() format
      }
      return fillcolor.length === 9 && fillcolor.startsWith('#') 
        ? fillcolor.slice(0, 7) 
        : fillcolor;
    }
    
    // Handle array colors - use first element or fallback to scalar
    let colorValue = trace.marker?.color ?? trace.fillcolor;
    if (Array.isArray(colorValue) && colorValue.length > 0) {
      colorValue = colorValue[0];
    }
    return colorValue || '#1f77b4';
  };
  
  // Update fillColor when trace changes (especially for Indicator traces and ridgeline plots)
  // Only update if the color actually changed to prevent infinite loops
  useEffect(() => {
    const extractedColor = extractColorFromTrace(currentTrace);
    const normalizedColor = normalizeColorValue(extractedColor, '#1f77b4');
    
    // Only update if the normalized color actually changed to prevent infinite loops
    if (normalizedColor !== prevColorRef.current) {
      prevColorRef.current = normalizedColor;
      // Preserve original format (rgba/hex) for ColorPicker display
      setFillColor(extractedColor);
    }
  }, [currentTrace]);
  
  // Data label state - computed directly from current trace to always reflect actual state
  // Covers all Plotly label configurations:
  // - Bar/Histogram: textmode (not 'none') OR (text array + textposition)
  // - Scatter/Line: mode includes 'text' OR (text array + textposition)
  // - Pie/Funnelarea/Treemap/Sunburst/Icicle: textinfo is set
  const hasDataLabels = useMemo(() => {
    const trace = currentTrace || {};
    const traceType = String(trace.type || '').toLowerCase();
    
    if (traceType === 'bar' || traceType === 'histogram') {
      // Bar charts: check textmode OR (text array + textposition)
      const hasTextMode = trace.textmode && trace.textmode !== 'none';
      const hasTextArray = Array.isArray(trace.text) && trace.text.length > 0;
      const hasTextPosition = trace.textposition !== undefined;
      return hasTextMode || (hasTextArray && hasTextPosition);
    } else if (traceType === 'scatter' || traceType === 'scattergl' || !trace.type) {
      // Scatter/Line: check mode includes 'text' OR (text array + textposition)
      const hasTextMode = trace.mode && typeof trace.mode === 'string' && trace.mode.includes('text');
      const hasTextArray = Array.isArray(trace.text) && trace.text.length > 0;
      const hasTextPosition = trace.textposition !== undefined;
      return hasTextMode || (hasTextArray && hasTextPosition);
    } else if (traceType === 'pie' || traceType === 'funnelarea' || traceType === 'treemap' || 
               traceType === 'sunburst' || traceType === 'icicle') {
      // Pie-like charts use textinfo instead of textmode
      return !!(trace.textinfo && trace.textinfo !== 'none');
    }
    return false;
  }, [currentTrace]);
  
  const isPie = String(currentTrace?.type || '').toLowerCase() === 'pie' || String(currentTrace?.type || '').toLowerCase() === 'funnelarea';
  const isWaterfall = String(currentTrace?.type || '').toLowerCase() === 'waterfall';
  const isCandlestick = String(currentTrace?.type || '').toLowerCase() === 'candlestick' || String(currentTrace?.type || '').toLowerCase() === 'ohlc';
  const isSankey = String(currentTrace?.type || '').toLowerCase() === 'sankey';
  const isIndicator = String(currentTrace?.type || '').toLowerCase() === 'indicator';
  const hasErrorBars = useMemo(() => {
    try {
      const t:any = currentTrace || {};
      return !!(t?.error_y || t?.error_x);
    } catch { return false; }
  }, [currentTrace]);
  const hasAnyErrorBars = useMemo(() => {
    try { return (traces as any[]).some(t => t && (t.error_y || t.error_x)); } catch { return false; }
  }, [traces]);
  const hasAnyErrorY = useMemo(() => {
    try { return (traces as any[]).some(t => t && t.error_y); } catch { return false; }
  }, [traces]);
  const hasAnyErrorX = useMemo(() => {
    try { return (traces as any[]).some(t => t && t.error_x); } catch { return false; }
  }, [traces]);
  const getFirstErrorColor = (axis: 'error_y'|'error_x'): string => {
    try {
      for (const t of traces as any[]) { if (t && t[axis] && t[axis].color) return t[axis].color; }
    } catch {}
    return '#333333';
  };
  const [errorYColor, setErrorYColor] = useState<string>(() => getFirstErrorColor('error_y'));
  const [errorXColor, setErrorXColor] = useState<string>(() => getFirstErrorColor('error_x'));
  useEffect(() => {
    setErrorYColor(getFirstErrorColor('error_y'));
    setErrorXColor(getFirstErrorColor('error_x'));
  }, [traces]);

  const handleSeriesUpdate = (property: string, value: any) => {
    // Update only the selected trace
    onUpdate(`series.${selectedTrace}.${property}`, value);
  };

  const handleSymbolChange = (symbol: string) => {
    setSelectedSymbol(symbol);
    // Apply symbol + ensure markers are enabled atomically to avoid lost updates
    setShowMarkers(true);
    
    // Open/unfilled symbols require marker.line.width > 0 to be visible
    // These include: cross, x, triangle-up, triangle-down, y-up, hexagon, star
    const openSymbols = ['cross', 'x', 'triangle-up', 'triangle-down', 'y-up', 'hexagon', 'star'];
    const isOpenSymbol = openSymbols.includes(symbol);
    
    const updates = [
      { type: `series.${selectedTrace}.marker.symbol`, value: symbol },
      { type: `series.${selectedTrace}.showMarkers`, value: true }
    ];
    
    // Ensure line width is set for open symbols
    if (isOpenSymbol) {
      const currentLineWidth = currentTrace.marker?.line?.width;
      // Set line width to 1 if it's 0 or undefined (open symbols need width > 0 to be visible)
      if (!currentLineWidth || currentLineWidth === 0) {
        updates.push({ type: `series.${selectedTrace}.marker.line.width`, value: 1 });
      }
    }
    
    onUpdate('batch.apply', updates);
  };

  // Removed lock/reset UX per request
  const LockRow = () => null as any;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <h4 className="text-lg font-semibold">Series</h4>
      </div>

      {/* Series Selector */}
      {traces.length > 1 && (
        <div>
          <Label className="text-sm text-muted-foreground">
            Select Series
          </Label>
          <Select value={selectedTrace.toString()} onValueChange={(v) => setSelectedTrace(parseInt(v))}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {traces.map((trace: any, idx: number) => (
                <SelectItem key={idx} value={idx.toString()}>
                  {trace.name || `Series ${idx + 1}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Symbol Shape - Only show for scatter/line plots with actual point data */}
      {(() => {
        // Check if trace supports markers and symbols
        const supportsMarkers = caps.supportsMarker && caps.supportsMarkerSymbol;
        const isScatterType = currentTrace.type === 'scatter' || !currentTrace.type || currentTrace.type === 'scattergl';
        
        // Check if there's actual point data (x and y arrays with data)
        const hasPointData = Array.isArray(currentTrace.x) && currentTrace.x.length > 0 && 
                            Array.isArray(currentTrace.y) && currentTrace.y.length > 0;
        
        // Only show if all conditions are met
        return supportsMarkers && isScatterType && hasPointData;
      })() && (
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">
            Point Symbol
          </Label>
          <div className="grid grid-cols-5 gap-1">
            {symbolOptions.map(({ value, label, Icon }) => (
              <button
                key={value}
                onClick={() => handleSymbolChange(value)}
                className={`p-2 border rounded hover:bg-accent transition-colors flex items-center justify-center ${
                  selectedSymbol === value ? 'bg-accent border-ring' : 'border-border'
                }`}
                title={label}
              >
                <Icon size={16} className="text-foreground" />
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="show-markers" className="text-sm">Show Points</Label>
            <Switch
              id="show-markers"
              checked={showMarkers}
              onCheckedChange={(checked) => {
                setShowMarkers(checked);
                handleSeriesUpdate('showMarkers', checked);
              }}
            />
          </div>
        </div>
      )}


      {/* Waterfall segment colors (increasing/decreasing/totals) – show early */}
      {caps.supportsWaterfallRoles && isWaterfall && (
        <div className="grid grid-cols-1 gap-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Increasing</Label>
            <ColorPicker value={currentTrace?.increasing?.marker?.color || '#4caf50'} onChange={(c) => handleSeriesUpdate('waterfall.increasing', c)} />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm">Decreasing</Label>
            <ColorPicker value={currentTrace?.decreasing?.marker?.color || '#f44336'} onChange={(c) => handleSeriesUpdate('waterfall.decreasing', c)} />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm">Totals</Label>
            <ColorPicker value={currentTrace?.totals?.marker?.color || '#9e9e9e'} onChange={(c) => handleSeriesUpdate('waterfall.totals', c)} />
          </div>
        </div>
      )}

      {/* Candlestick/OHLC colors (increasing/decreasing) – show early */}
      {caps.supportsFinancialRoles && isCandlestick && (
        <div className="grid grid-cols-1 gap-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Increasing (Up)</Label>
            <ColorPicker 
              value={currentTrace?.increasing?.line?.color || currentTrace?.increasing?.fillcolor || '#228833'} 
              onChange={(c) => handleSeriesUpdate('candlestick.increasing', c)} 
            />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm">Decreasing (Down)</Label>
            <ColorPicker 
              value={currentTrace?.decreasing?.line?.color || currentTrace?.decreasing?.fillcolor || '#EE6677'} 
              onChange={(c) => handleSeriesUpdate('candlestick.decreasing', c)} 
            />
          </div>
        </div>
      )}

      {/* Fill Color - per-trace fill only when supported */}
      {caps.supportsPerTraceFill && !isPie && !isWaterfall && !isCandlestick && (
        <ColorPicker
          label={isIndicator ? "Bar Color" : "Fill Color (Inside)"}
          value={fillColor}
          onChange={(color) => {
            // Normalize color to hex for consistent state management
            // ColorPicker can handle hex even with includeOpacity=true
            const normalizedColor = normalizeColorValue(color, '#1f77b4');
            setFillColor(normalizedColor);
            prevColorRef.current = normalizedColor;
            // Send the original color (rgba if opacity < 100%) to the mutation router
            onUpdate('op.perTraceFill:set', { index: selectedTrace, color });
          }}
          includeOpacity={true}
        />
      )}

      {/* Indicator-specific controls: Step colors (background bands) and threshold line */}
      {isIndicator && currentTrace.gauge && (
        <div className="space-y-3 pt-2 border-t">
          {/* Step colors (poor/satisfactory/good bands) */}
          {Array.isArray(currentTrace.gauge.steps) && currentTrace.gauge.steps.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Background Bands</Label>
              <div className="grid grid-cols-1 gap-2">
                {currentTrace.gauge.steps.map((step: any, stepIdx: number) => {
                  const stepLabels = ['Poor', 'Satisfactory', 'Good'];
                  const stepLabel = stepLabels[stepIdx] || `Band ${stepIdx + 1}`;
                  return (
                    <div key={stepIdx} className="flex items-center justify-between">
                      <Label className="text-sm">{stepLabel}</Label>
                      <ColorPicker
                        value={step.color || '#cccccc'}
                        onChange={(c) => onUpdate('op.indicatorStepColor:set', { 
                          index: selectedTrace, 
                          stepIndex: stepIdx, 
                          color: c 
                        })}
                        includeOpacity={false}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Threshold line color (target marker) */}
          {currentTrace.gauge.threshold && (
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Target Line</Label>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Color</Label>
                <ColorPicker
                  value={currentTrace.gauge.threshold.line?.color || '#332288'}
                  onChange={(c) => onUpdate('op.indicatorThresholdColor:set', { 
                    index: selectedTrace, 
                    color: c 
                  })}
                  includeOpacity={false}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {caps.supportsLine && (currentTrace.type === 'scatter' || currentTrace.type === 'scattergl') && (
        <div className="flex items-center justify-between">
          <Label className="text-sm">Decouple line from marker</Label>
          <Switch
            checked={decoupleLine}
            onCheckedChange={(checked) => {
              setDecoupleLine(checked);
              handleSeriesUpdate('line.decoupled', checked);
            }}
          />
        </div>
      )}

      {/* Pie/Funnelarea slice colors */}
      {caps.supportsSliceColors && isPie && Array.isArray(currentTrace.labels) && (
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">Slice colors</Label>
          <div className="grid grid-cols-2 gap-2">
            {(currentTrace.labels as any[]).map((lab: any, i: number) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs w-12 truncate" title={String(lab)}>{String(lab)}</span>
                <ColorPicker
                  value={currentTrace?.marker?.colors?.[i] || fillColor}
                  onChange={(c) => onUpdate('op.sliceColor:set', { index: selectedTrace, sliceIndex: i, color: c })}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sankey node colors */}
      {caps.supportsNodeColors && isSankey && Array.isArray(currentTrace?.node?.label || currentTrace?.node?.labels) && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm text-muted-foreground">Node colors</Label>
            {(currentTrace as any)?._lumiTheme?.lockedNodeColors && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs"
                onClick={() => onUpdate('op.sankeyResetTheme:set', { index: selectedTrace })}
              >
                Reset to theme
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
            {(currentTrace.node.label || currentTrace.node.labels || []).map((lab: any, i: number) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs w-16 truncate" title={String(lab)}>{String(lab)}</span>
                <ColorPicker
                  value={currentTrace?.node?.color?.[i] || fillColor}
                  onChange={(c) => onUpdate('op.sankeyNodeColor:set', { index: selectedTrace, nodeIndex: i, color: c })}
                />
              </div>
            ))}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Link colors follow node colors from theme
          </div>
        </div>
      )}

      {/* Line Color - Controls the outline/border (pie/funnelarea uses marker.line) */}
      {caps.supportsLine && (
        <ColorPicker
          label="Line Color (Outline)"
          value={lineColor}
          onChange={(color) => {
            setLineColor(color);
            handleSeriesUpdate('line.color', color);
          }}
          includeOpacity={true}
        />
      )}

      {/* Line Width - Per-series control */}
      {caps.supportsLine && (
        <div>
          <Label className="text-sm text-muted-foreground">
            Line Width (selected series)
          </Label>
          <div className="mt-1 space-y-2">
            <Slider
              value={[lineWidth]}
              onValueChange={([v]) => {
                setLineWidth(v);
                handleSeriesUpdate('line.width', v);
              }}
              min={0}
              max={10}
              step={0.5}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>None</span>
              <span>{lineWidth}px</span>
              <span>Thick</span>
            </div>
          </div>
        </div>
      )}

      {/* Waterfall segment colors block moved above (avoid duplicate) */}

      {/* Item Size (hide for pie/funnelarea/waterfall) */}
      {caps.supportsMarker && !isPie && !isWaterfall && (
        <div>
          <Label className="text-sm text-muted-foreground">
            Item Size
          </Label>
          <div className="mt-1 space-y-2">
            <Slider
              value={[itemSize]}
              onValueChange={([v]) => {
                setItemSize(v);
                handleSeriesUpdate('itemSize', v);
              }}
              min={2}
              max={20}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Small</span>
              <span>{itemSize}</span>
              <span>Large</span>
            </div>
          </div>
        </div>
      )}

      {/* Gap (Universal for all plot types) */}
      <div>
        <Label className="text-sm text-muted-foreground">
          Gap
        </Label>
        <div className="mt-1 space-y-2">
          <Slider
            value={[gap]}
            onValueChange={([v]) => {
              setGap(v);
              // Gap affects layout, not individual series, so use special path
              onUpdate('series.all.gap', v);
            }}
            min={0}
            max={0.5}
            step={0.05}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>None</span>
            <span>{Math.round(gap * 100)}%</span>
            <span>Wide</span>
          </div>
        </div>
      </div>

      {/* Data Labels - Show for bar, histogram, scatter, line plots, and pie-like charts */}
      {(currentTrace.type === 'bar' || currentTrace.type === 'histogram' || currentTrace.type === 'scatter' || currentTrace.type === 'scattergl' || !currentTrace.type || isPie) && (
        <div className="space-y-3 pt-3 border-t">
          <div className="flex items-center justify-between">
            <Label htmlFor="show-data-labels" className="text-sm">Data Labels</Label>
            <Switch
              id="show-data-labels"
              checked={hasDataLabels}
              onCheckedChange={(checked) => {
                if (checked) {
                  // Enable data labels - always use 'restore' to preserve original configuration
                  // The handler will restore original textmode/mode if it exists, otherwise enable with defaults
                  handleSeriesUpdate('textmode', 'restore');
                } else {
                  // Disable data labels - this will store the current textmode/mode as original
                  handleSeriesUpdate('textmode', 'none');
                }
              }}
            />
          </div>
          
          {/* Label Font Size - Only show when labels are enabled */}
          {hasDataLabels && (
            <div>
              <Label className="text-sm text-muted-foreground">
                Label Font Size
              </Label>
              <div className="mt-1 space-y-2">
                <Slider
                  value={[labelFontSize]}
                  onValueChange={([v]) => {
                    setLabelFontSize(v);
                    handleSeriesUpdate('textfont.size', v);
                  }}
                  min={8}
                  max={24}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Small</span>
                  <span>{labelFontSize}px</span>
                  <span>Large</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Legend Settings */}
      <div className="space-y-3 pt-3 border-t">
        {/* Error bar colors (CI/SE etc.) */}
        {(hasAnyErrorBars || hasErrorBars) && (
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Error bars</Label>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Y error color</Label>
              {hasAnyErrorY ? (
                <ColorPicker
                  value={errorYColor}
                  onChange={(c) => {
                    setErrorYColor(c);
                    // Apply to all traces that have Y error bars
                    onUpdate('series.all.error_y.color', c);
                  }}
                />
              ) : (
                <div className="text-xs text-muted-foreground">-</div>
              )}
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">X error color</Label>
              {hasAnyErrorX ? (
                <ColorPicker
                  value={errorXColor}
                  onChange={(c) => {
                    setErrorXColor(c);
                    // Apply to all traces that have X error bars
                    onUpdate('series.all.error_x.color', c);
                  }}
                />
              ) : (
                <div className="text-xs text-muted-foreground">-</div>
              )}
            </div>
          </div>
        )}

        <h5 className="text-sm text-muted-foreground">Legend</h5>
        <LockRow />
        
        <div>
          <Label className="text-xs">Series Name</Label>
          <Input
            className="mt-1"
            defaultValue={currentTrace.name || `Series ${selectedTrace + 1}`}
            onChange={(e) => handleSeriesUpdate('name', e.target.value)}
            placeholder="Enter series name"
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="show-legend" className="text-sm">Show in Legend</Label>
          <Switch
            id="show-legend"
            defaultChecked={currentTrace.showlegend !== false}
            onCheckedChange={(checked) => handleSeriesUpdate('showlegend', checked)}
          />
        </div>

        {/* Reset to theme removed per request */}
      </div>
    </div>
  );
}